<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    v-bind="$attrs"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <slot />
  </button>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'Button',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'default'
    },
    size: {
      type: String,
      default: 'default'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const buttonClasses = computed(() => {
      const baseClasses = 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 touch-target'
      
      const variantClasses = {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-enhanced active:scale-95',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-enhanced active:scale-95',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:shadow-enhanced active:scale-95',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-enhanced active:scale-95',
        ghost: 'hover:bg-accent hover:text-accent-foreground active:scale-95',
        link: 'text-primary underline-offset-4 hover:underline active:scale-95'
      }
      
      const sizeClasses = {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10'
      }
      
      return [
        baseClasses,
        variantClasses[props.variant] || variantClasses.default,
        sizeClasses[props.size] || sizeClasses.default
      ].join(' ')
    })

    const handleClick = (event) => {
      if (!props.disabled) {
        // Add ripple effect
        const button = event.currentTarget
        const ripple = document.createElement('span')
        const rect = button.getBoundingClientRect()
        const size = Math.max(rect.width, rect.height)
        const x = event.clientX - rect.left - size / 2
        const y = event.clientY - rect.top - size / 2
        
        ripple.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          left: ${x}px;
          top: ${y}px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.6s linear;
          pointer-events: none;
        `
        
        button.style.position = 'relative'
        button.style.overflow = 'hidden'
        button.appendChild(ripple)
        
        setTimeout(() => {
          ripple.remove()
        }, 600)
        
        emit('click', event)
      }
    }

    const handleKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleClick(event)
      }
    }

    return {
      buttonClasses,
      handleClick,
      handleKeydown
    }
  }
}
</script>